import React, { useCallback, useEffect } from "react";
import { ConfigAppSDK } from "@contentful/app-sdk";
import {
  Heading,
  Form,
  Paragraph,
  TextInput,
  Notification,
  FormControl,
  Radio,
} from "@contentful/f36-components";
import logo from "../logo.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface AppInstallationParameters {}

interface ConfigProps {
  sdk: ConfigAppSDK;
}

const Config = (props: ConfigProps) => {
  // App Parameters
  const [parameters, setParameters] = React.useState<AppInstallationParameters>({});
  const [authToken, setAuthToken] = React.useState("");
  const [secretKey, setSecretKey] = React.useState("");
  const [exportFormat, setExportFormat] = React.useState("");
  const [companyId, setCompanyId] = React.useState("");
  const [cmaToken, setCmaToken] = React.useState("");


  const onChangeSecretKey = (newSecretKey: string) => {
    let params: any = parameters;
    params.secretKey = newSecretKey;
    setSecretKey(newSecretKey);
  };

  const onChangeAuthToken = (newAuthToken: string) => {
    let params: any = parameters;
    params.authToken = newAuthToken;
    setAuthToken(newAuthToken);
  };

  const onSelectExportFormat = (checked: boolean, format: string) => {
    let params: any = parameters;
    params.exportFormat = format;
    setExportFormat(format);
  };

  const onChangeCompanyId = (newCompanyId: string) => {
    let params: any = parameters;
    params.companyId = newCompanyId;
    setCompanyId(newCompanyId);
  };

  const onChangeCmaToken = (newCmaToken: string) => {
    let params: any = parameters;
    params.cmaToken = newCmaToken;
    setCmaToken(newCmaToken);
  };

  const onConfigure = useCallback(async () => {
    // This method will be called when a user clicks on "Install"
    // or "Save" in the configuration screen.
    // for more details see https://www.contentful.com/developers/docs/extensibility/ui-extensions/sdk-reference/#register-an-app-configuration-hook

    // Get current the state of EditorInterface and other entities
    // related to this app installation
    const currentState = await props.sdk.app.getCurrentState();

    // Validations
    let params: any = parameters;

    if (!params.secretKey || !params.secretKey.trim()) {
      Notification.setDuration(2000);
      Notification.error("Please, specify a Secret Key.");
      return false;
    }

    if (!params.authToken || !params.authToken.trim()) {
      Notification.setDuration(2000);
      Notification.error("Please, specify an Auth Token.");
      return false;
    }

    if (!params.cmaToken || !params.cmaToken.trim()) {
      Notification.setDuration(2000);
      Notification.error("Please, specify an CMA Token.");
      return false;
    }

    // Save configuration to MongoDB (with encrypted CMA token)
    try {
      const response = await fetch(`${BACKEND_URL}/api/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          spaceId: props.sdk.ids.space,
          environmentId: props.sdk.ids.environment,
          cmaToken: params.cmaToken,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        console.error('Failed to save configuration to MongoDB:', result.error);
        Notification.setDuration(3000);
        Notification.warning("Configuration saved but failed to sync with backend: " + result.error);
      }
    } catch (error: any) {
      console.error('Error saving configuration to MongoDB:', error);
      Notification.setDuration(3000);
      Notification.warning("Configuration saved but failed to sync with backend: " + error.message);
    }

    return {
      // Parameters to be persisted as the app configuration.
      parameters,
      // In case you don't want to submit any update to app
      // locations, you can just pass the currentState as is
      targetState: currentState,
    };
  }, [parameters, props.sdk]);

  useEffect(() => {
    // `onConfigure` allows to configure a callback to be
    // invoked when a user attempts to install the app or update
    // its configuration.
    props.sdk.app.onConfigure(() => onConfigure());
  }, [props.sdk, onConfigure]);

  useEffect(() => {
    (async () => {
      // Get current parameters of the app.
      // If the app is not installed yet, `parameters` will be `null`.
      const currentParameters: AppInstallationParameters | null = await props.sdk.app.getParameters();

      if (currentParameters) {
        setParameters(currentParameters);
        let params: any = currentParameters;
        setAuthToken(params.authToken);
        setSecretKey(params.secretKey);
        setExportFormat(params.exportFormat ? params.exportFormat : "json");
        setCompanyId(params.companyId);
        setCmaToken(params.cmaToken);
      }

      // Once preparation has finished, call `setReady` to hide
      // the loading screen and present the app to a user.
      props.sdk.app.setReady();
    })();
  }, [props.sdk]);

  return (
    <div className="config-content">
        <div className="config-background"></div>
        <div className="config-container">
          <Form>
            <Heading>About GPI Translation Services Connector</Heading>
            <Paragraph>
              The GPI Translation Services Connector enables seamless translation workflows allowing users of Contentful
              to select, export and import web content for translation with just a few clicks.
            </Paragraph>
            <hr />
            <Heading>Configuration</Heading>
            <Paragraph>
              Please, enter the following parameters to get the connector working. If you don't have these parameters,
              please contact <b>support@globalizationpartners.com</b>.
            </Paragraph>
            <div className="f36-margin-bottom--m">
              <Paragraph className="f36-margin-bottom--m f36-margin-top--m">
                <b>Secret Key</b> (required)
              </Paragraph>
              <TextInput
                name="secret-key"
                type="text"
                value={secretKey}
                onChange={(e) => onChangeSecretKey(e.target.value)}
              />
            </div>

            <div className="f36-margin-bottom--m" style={{ marginTop: '24px' }}>
              <Paragraph className="f36-margin-bottom--m">
                <b>Auth Token</b> (required)
              </Paragraph>
              <TextInput
                name="auth-token"
                type="text"
                value={authToken}
                onChange={(e) => onChangeAuthToken(e.target.value)}
              />
            </div>
            <div className="f36-margin-bottom--m" style={{ marginTop: '24px' }}>
              <Paragraph className="f36-margin-bottom--m">
                <b>Company ID:</b>
              </Paragraph>
              <TextInput
                name="companyId"
                type="text"
                value={companyId}
                onChange={(e) => onChangeCompanyId(e.target.value)}
              />
            </div>
            <div className="f36-margin-bottom--m" style={{ marginTop: '24px' }}>
              <Paragraph className="f36-margin-bottom--m">
                <b>CMA Token</b> (required)
              </Paragraph>
              <TextInput
                name="cmaToken"
                type="text"
                value={cmaToken}
                onChange={(e) => onChangeCmaToken(e.target.value)}
                isRequired
              />
            </div>
            <FormControl as="fieldset" className="f36-margin-bottom--m" style={{ marginTop: '24px' }}>
              <FormControl.Label as="legend" className="f36-margin-bottom--m">
                <b>Export format</b> (required)
              </FormControl.Label>
              <Radio.Group
                name="export-format"
                value={exportFormat || "json"}
                onChange={(e) => {
                  const format = e.target.value;
                  setExportFormat(format);
                  let params: any = parameters;
                  params.exportFormat = format;
                }}
              >
                <div style={{ display: "inline-block", marginRight: "20px" }}>
                  <Radio value="json" id="rb-format-json">JSON</Radio>
                </div>
                <div style={{ display: "inline-block", marginRight: "20px" }}>
                  <Radio value="xliff" id="rb-format-xliff">XLIFF</Radio>
                </div>
              </Radio.Group>
            </FormControl>
          </Form>
        </div>
        <div className="config-footer">
          <img src={logo} alt="App logo" />
        </div>
      </div>
  );
};

export default Config;
